export function filterTranscriptionBlocksBySearchResults(searchResults, transcriptBlocks) {
    if (!searchResults || searchResults.length === 0) {
        return {
            filteredBlocks: transcriptBlocks,
            matchingSearchTerms: [],
        };
    }
    // wrap the results of the flatmap into a set to remove duplicates and then spread back into an array
    const matchingSearchTerms = [
        ...new Set(searchResults.flatMap(searchResult => searchResult.terms)),
    ];
    const resultIds = new Set(searchResults.map(searchResult => searchResult.id));
    const filteredBlocks = transcriptBlocks.filter(transcriptBlock => resultIds.has(transcriptBlock.id));
    return {
        filteredBlocks,
        matchingSearchTerms,
    };
}
/*
 * This function is intended to be used to split up a block of text into segments that match one of the terms in
 * a list of terms and segments that don't match. The function returns an array of Parts, each of which has a
 * text property and a match property. The match property is true if the text segment matches one of the terms in
 * the list. The results can then be used to support features like text highlighting search results.
 *   * case-insensitive matching
 *   * matches anywhere, not just at word boundaries
 *   * preserves original casing
 *   * longest-first sorting
 * e.g.
 *      text = 'I am a plumber that is here to plumb your plumbing';
        searchTerms = ['plumb', 'plumbing', 'plumber'];
        const result = splitTextBySearchTerms(text, searchTerms);
        expect(result).toEqual([
            { text: 'I am a ', match: false },
            { text: 'plumber', match: true },
            { text: ' that is here to ', match: false },
            { text: 'plumb', match: true },
            { text: ' your ', match: false },
            { text: 'plumbing', match: true },
        ]);
 */
export function splitTextBySearchTerms(text, searchTerms) {
    // If there aren't any search terms, then bail
    if (searchTerms.length === 0) {
        return [{ text, match: false }];
    }
    // Sorting by length (longest first) handles overlapping terms correctly by prioritizing longer matches
    const sortedLowerTerms = [...searchTerms]
        .map(term => term.toLowerCase())
        .sort((a, b) => b.length - a.length);
    const results = [];
    let lastMatchEnd = 0;
    const lowerText = text.toLowerCase();
    let cursorPosition = 0;
    while (cursorPosition < text.length) {
        let matched = false;
        // Check if any term matches at current position
        for (const term of sortedLowerTerms) {
            if (lowerText.substring(cursorPosition, cursorPosition + term.length) === term) {
                // Add text before match
                if (cursorPosition > lastMatchEnd) {
                    results.push({
                        text: text.slice(lastMatchEnd, cursorPosition),
                        match: false,
                    });
                }
                // Add match (preserve original case)
                results.push({
                    text: text.slice(cursorPosition, cursorPosition + term.length),
                    match: true,
                });
                cursorPosition += term.length;
                lastMatchEnd = cursorPosition;
                matched = true;
                break;
            }
        }
        if (!matched)
            cursorPosition++;
    }
    // Add remaining text with match = false
    if (lastMatchEnd < text.length) {
        results.push({
            text: text.slice(lastMatchEnd),
            match: false,
        });
    }
    // Return results with any potential empty segments removed
    return results.filter(segment => segment.text.length > 0);
}
//# sourceMappingURL=transcript_utils.js.map